import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { theme } from '../config/theme';
import { instructors } from '../config/instructors';
import { Button } from '../junto-frontend/components/ui';

const SectionWrapper = styled(motion.section)`
  padding: 120px 0;
  max-width: 1400px;
  margin: 0 auto;
  padding-left: 80px;
  padding-right: 80px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 80px 40px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 20px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 120px;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const TextColumn = styled(motion.div)`
  order: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    order: 2;
    text-align: center;
  }
`;

const GalleryColumn = styled(motion.div)`
  order: 2;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    order: 1;
  }
`;

const Heading = styled(motion.h2)`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.largeHeading.fontSize};
  font-weight: ${theme.typography.primary.largeHeading.fontWeight};
  line-height: ${theme.typography.primary.largeHeading.lineHeight};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.xl};
  text-align: left;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    text-align: center;
  }
  
  em {
    font-family: ${theme.typography.primary.fontFamily};
    font-style: italic;
    color: ${theme.colors.primary.text};
  }
`;

const Description = styled(motion.p)`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  font-weight: ${theme.typography.secondary.body.fontWeight};
  line-height: ${theme.typography.secondary.body.lineHeight};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.xl};
  text-align: left;
  max-width: 480px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 100%;
    text-align: center;
  }
`;

const InstructorGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 140px;
  gap: 16px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 120px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 140px;
  }
`;

const InstructorCard = styled(motion.div)`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: ${theme.transitions.default};
  
  &:hover {
    transform: scale(1.02);
  }
  
  /* Size variations */
  ${props => props.size === 'large' && `
    grid-column: span 2;
    grid-row: span 2;
  `}
  
  ${props => props.size === 'medium' && `
    grid-column: span 1;
    grid-row: span 2;
  `}
  
  ${props => props.size === 'small' && `
    grid-column: span 1;
    grid-row: span 1;
  `}
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    ${props => props.size === 'large' && `
      grid-column: span 2;
      grid-row: span 2;
    `}
    
    ${props => props.size === 'medium' && `
      grid-column: span 1;
      grid-row: span 2;
    `}
    
    ${props => props.size === 'small' && `
      grid-column: span 1;
      grid-row: span 1;
    `}
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    /* On mobile, all cards take up 1 column and 1 row */
    grid-column: span 1 !important;
    grid-row: span 1 !important;
  }
`;

const InstructorImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: ${theme.transitions.default};
`;

const InstructorOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
  color: white;
  padding: 20px;
  transform: translateY(100%);
  transition: ${theme.transitions.default};
  
  ${InstructorCard}:hover & {
    transform: translateY(0);
  }
`;

const InstructorName = styled.h3`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: 1.2rem;
  font-weight: 300;
  margin-bottom: 4px;
  color: white;
`;

const InstructorTitle = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const InstructorSection = ({ ...props }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0, // No stagger - text and images appear simultaneously
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const galleryVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <SectionWrapper
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      {...props}
    >
      <Grid>
        <TextColumn>
          <Heading 
            variants={itemVariants}
            dangerouslySetInnerHTML={{ __html: "Meet Our <em>Instructors</em>" }}
          />
          <Description variants={itemVariants}>
            Certified instructors with decades of experience and shared passion. Unique teaching styles unified by our commitment to excellence, safety, and personalized attention.
          </Description>
          <motion.div variants={itemVariants}>
            <Button 
              as={Link}
              to="/join-our-team"
            >
              Join Our Team
            </Button>
          </motion.div>
        </TextColumn>
        
        <GalleryColumn>
          <InstructorGallery variants={galleryVariants}>
            {instructors.map((instructor, index) => (
              <InstructorCard 
                key={index}
                size={instructor.size}
                variants={cardVariants}
                as={Link}
                to={`/instructor/${instructor.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <InstructorImage 
                  src={instructor.photo} 
                  alt={instructor.name}
                />
                <InstructorOverlay>
                  <InstructorName>{instructor.name}</InstructorName>
                  <InstructorTitle>{instructor.title}</InstructorTitle>
                </InstructorOverlay>
              </InstructorCard>
            ))}
          </InstructorGallery>
        </GalleryColumn>
      </Grid>
    </SectionWrapper>
  );
};

export default InstructorSection;